import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { 
  ArrowLeft, 
  ArrowRight, 
  Users, 
  Briefcase, 
  Home, 
  Plane, 
  Heart,
  DollarSign,
  AlertTriangle,
  CheckCircle2,
  FileSearch,
  Info,
  Send,
  Loader2,
  Calendar,
  MapPin,
  UserPlus,
  Trash2,
  Baby,
  GraduationCap
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { Checkbox } from "../../components/ui/checkbox";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import DashboardLayout from "../../components/DashboardLayout";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuestionnaireStep = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [caseData, setCaseData] = useState(null);
  const [currentSection, setCurrentSection] = useState(0);
  const [showFOIAModal, setShowFOIAModal] = useState(false);
  const [foiaLoading, setFoiaLoading] = useState(false);
  const [foiaInfo, setFoiaInfo] = useState(null);
  const [foiaFormData, setFoiaFormData] = useState({
    denial_date: "",
    consulate: "",
    denial_reason: "",
    additional_details: ""
  });
  const [formData, setFormData] = useState({
    // Family info
    has_family_in_usa: false,
    family_members: [],
    family_relationship: "",
    family_legal_entry: "",
    family_current_status: "",
    family_years_in_usa: "",
    family_has_petition: false,
    family_citizen_or_resident: "",
    // Travel companions
    traveling_alone: true,
    num_travelers: 1,
    travelers: [],
    has_minor_children: false,
    children_traveling: "",
    children_staying_with: "",
    // Other info
    was_denied_before: false,
    monthly_income: 0,
    employment_status: "",
    previous_travels: 0,
    marital_status: "",
    owns_property: false,
    travel_purpose: "",
    additional_info: ""
  });

  useEffect(() => {
    fetchCase();
    fetchFOIAInfo();
  }, []);

  const fetchCase = async () => {
    try {
      const res = await axios.get(`${API}/cases/my-case`);
      setCaseData(res.data);
      if (res.data?.questionnaire_data) {
        // Merge with defaults to ensure new fields exist
        setFormData(prev => ({
          ...prev,
          ...res.data.questionnaire_data,
          // Ensure arrays are properly initialized
          travelers: res.data.questionnaire_data.travelers || [],
          family_members: res.data.questionnaire_data.family_members || []
        }));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchFOIAInfo = async () => {
    try {
      const res = await axios.get(`${API}/foia/info`);
      setFoiaInfo(res.data);
    } catch (error) {
      console.error("Error fetching FOIA info:", error);
    }
  };

  const handleFOIARequest = async () => {
    if (!foiaFormData.denial_date || !foiaFormData.consulate) {
      toast.error("Por favor completa la fecha y el consulado de la negación");
      return;
    }

    setFoiaLoading(true);
    try {
      await axios.post(`${API}/foia/request`, foiaFormData);
      toast.success("¡Solicitud FOIA enviada! Un asesor se pondrá en contacto contigo.");
      setShowFOIAModal(false);
      setFoiaFormData({ denial_date: "", consulate: "", denial_reason: "", additional_details: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar la solicitud");
    } finally {
      setFoiaLoading(false);
    }
  };

  const sections = [
    {
      title: "Información Familiar",
      icon: Users,
      fields: ["has_family_in_usa", "marital_status"]
    },
    {
      title: "Situación Laboral",
      icon: Briefcase,
      fields: ["employment_status", "monthly_income"]
    },
    {
      title: "Patrimonio y Viajes",
      icon: Home,
      fields: ["owns_property", "previous_travels"]
    },
    {
      title: "Propósito del Viaje",
      icon: Plane,
      fields: ["travel_purpose", "was_denied_before", "additional_info"]
    }
  ];

  // Auto-save debounce ref
  const saveTimeoutRef = useRef(null);
  
  // Auto-save function
  const autoSave = useCallback(async (data) => {
    if (!caseData?.id) return;
    try {
      await axios.put(`${API}/cases/${caseData.id}/questionnaire`, data);
      console.log("Auto-saved questionnaire data");
    } catch (error) {
      console.error("Auto-save error:", error);
    }
  }, [caseData?.id]);
  
  const handleChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);
    
    // Debounced auto-save (save 2 seconds after last change)
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      autoSave(newFormData);
    }, 2000);
  };
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const handleSubmit = async () => {
    if (!formData.employment_status || !formData.marital_status || !formData.travel_purpose) {
      toast.error("Por favor completa todos los campos requeridos");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/cases/${caseData.id}/questionnaire`, formData);
      toast.success(`Cuestionario completado. Probabilidad de aprobación: ${res.data.probability_score}%`);
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar el cuestionario");
    } finally {
      setLoading(false);
    }
  };

  const nextSection = async () => {
    // Auto-save progress when moving to next section
    if (caseData?.id) {
      try {
        // Use PUT endpoint for partial save
        await axios.put(`${API}/cases/${caseData.id}/questionnaire`, formData);
        console.log("Questionnaire progress saved");
      } catch (error) {
        console.error("Error saving progress:", error);
        // Don't block navigation on save error
      }
    }
    
    if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
    }
  };

  const prevSection = async () => {
    // Also save when going back
    if (caseData?.id) {
      try {
        await axios.put(`${API}/cases/${caseData.id}/questionnaire`, formData);
      } catch (error) {
        console.error("Error saving progress:", error);
      }
    }
    
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Button 
            variant="ghost" 
            onClick={() => navigate("/dashboard")}
            className="mb-4 text-navy-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al Dashboard
          </Button>
          <h1 className="font-heading text-3xl font-bold text-navy-900 mb-2">
            Cuestionario Estratégico
          </h1>
          <p className="text-navy-600">
            Responde estas preguntas para evaluar tu perfil y calcular tu probabilidad de aprobación.
          </p>
        </motion.div>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-8">
          {sections.map((section, index) => (
            <div key={index} className="flex items-center">
              <button
                onClick={() => setCurrentSection(index)}
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                  index === currentSection
                    ? "bg-gold-600 text-white"
                    : index < currentSection
                    ? "bg-emerald-500 text-white"
                    : "bg-navy-200 text-navy-500"
                }`}
              >
                {index < currentSection ? (
                  <CheckCircle2 className="w-5 h-5" />
                ) : (
                  <section.icon className="w-5 h-5" />
                )}
              </button>
              {index < sections.length - 1 && (
                <div className={`w-12 sm:w-20 h-1 mx-2 ${
                  index < currentSection ? "bg-emerald-500" : "bg-navy-200"
                }`}></div>
              )}
            </div>
          ))}
        </div>

        {/* Form Card */}
        <motion.div
          key={currentSection}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="card-elevated p-6 sm:p-8"
        >
          <div className="flex items-center gap-3 mb-6">
            {(() => {
              const Icon = sections[currentSection].icon;
              return (
                <div className="w-12 h-12 bg-gold-100 rounded-xl flex items-center justify-center">
                  <Icon className="w-6 h-6 text-gold-600" />
                </div>
              );
            })()}
            <div>
              <h2 className="font-heading text-xl font-bold text-navy-900">
                {sections[currentSection].title}
              </h2>
              <p className="text-sm text-navy-500">Sección {currentSection + 1} de {sections.length}</p>
            </div>
          </div>

          {/* Section 0: Family */}
          {currentSection === 0 && (
            <div className="space-y-6">
              <div className="space-y-4">
                <Label className="text-navy-700 font-medium">¿Tienes familiares viviendo en Estados Unidos?</Label>
                <RadioGroup
                  value={formData.has_family_in_usa ? "yes" : "no"}
                  onValueChange={(val) => handleChange("has_family_in_usa", val === "yes")}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="family-yes" data-testid="family-yes" />
                    <Label htmlFor="family-yes">Sí</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="family-no" data-testid="family-no" />
                    <Label htmlFor="family-no">No</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Expanded family questions */}
              <AnimatePresence>
                {formData.has_family_in_usa && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-5 overflow-hidden"
                  >
                    {/* Info banner */}
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-start gap-3">
                        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-blue-800">
                          Esta información es <strong>confidencial</strong> y nos ayuda a evaluar mejor tu perfil migratorio. 
                          La situación de tus familiares puede influir en la decisión del oficial consular.
                        </p>
                      </div>
                    </div>

                    {/* Relationship */}
                    <div className="space-y-3">
                      <Label className="text-navy-700 font-medium">¿Qué parentesco tienes con ellos? *</Label>
                      <Select 
                        value={formData.family_relationship} 
                        onValueChange={(val) => handleChange("family_relationship", val)}
                      >
                        <SelectTrigger data-testid="family-relationship-select">
                          <SelectValue placeholder="Selecciona el parentesco más cercano" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="parents">Padres</SelectItem>
                          <SelectItem value="siblings">Hermanos</SelectItem>
                          <SelectItem value="children">Hijos</SelectItem>
                          <SelectItem value="spouse">Cónyuge</SelectItem>
                          <SelectItem value="uncles_aunts">Tíos</SelectItem>
                          <SelectItem value="cousins">Primos</SelectItem>
                          <SelectItem value="grandparents">Abuelos</SelectItem>
                          <SelectItem value="other">Otros familiares</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Legal entry */}
                    <div className="space-y-3">
                      <Label className="text-navy-700 font-medium">¿Cómo ingresaron a Estados Unidos? *</Label>
                      <RadioGroup
                        value={formData.family_legal_entry}
                        onValueChange={(val) => handleChange("family_legal_entry", val)}
                        className="space-y-2"
                      >
                        <div className="flex items-center space-x-3 p-3 rounded-lg border border-navy-200 hover:border-navy-300 transition-colors">
                          <RadioGroupItem value="legal_visa" id="entry-legal-visa" />
                          <Label htmlFor="entry-legal-visa" className="flex-1 cursor-pointer">
                            <span className="font-medium text-navy-800">Con visa válida</span>
                            <p className="text-xs text-navy-500">Entraron con visa de turista, trabajo, estudiante, etc.</p>
                          </Label>
                        </div>
                        <div className="flex items-center space-x-3 p-3 rounded-lg border border-navy-200 hover:border-navy-300 transition-colors">
                          <RadioGroupItem value="legal_other" id="entry-legal-other" />
                          <Label htmlFor="entry-legal-other" className="flex-1 cursor-pointer">
                            <span className="font-medium text-navy-800">Otro método legal</span>
                            <p className="text-xs text-navy-500">Asilo, refugio, programa especial, etc.</p>
                          </Label>
                        </div>
                        <div className="flex items-center space-x-3 p-3 rounded-lg border border-amber-200 bg-amber-50 hover:border-amber-300 transition-colors">
                          <RadioGroupItem value="illegal" id="entry-illegal" />
                          <Label htmlFor="entry-illegal" className="flex-1 cursor-pointer">
                            <span className="font-medium text-amber-800">Sin documentos</span>
                            <p className="text-xs text-amber-700">Cruzaron la frontera sin autorización</p>
                          </Label>
                        </div>
                        <div className="flex items-center space-x-3 p-3 rounded-lg border border-navy-200 hover:border-navy-300 transition-colors">
                          <RadioGroupItem value="unknown" id="entry-unknown" />
                          <Label htmlFor="entry-unknown" className="flex-1 cursor-pointer">
                            <span className="font-medium text-navy-800">No lo sé</span>
                            <p className="text-xs text-navy-500">Desconozco cómo entraron</p>
                          </Label>
                        </div>
                      </RadioGroup>
                    </div>

                    {/* Current status */}
                    <div className="space-y-3">
                      <Label className="text-navy-700 font-medium">¿Cuál es su estatus migratorio actual? *</Label>
                      <Select 
                        value={formData.family_current_status} 
                        onValueChange={(val) => handleChange("family_current_status", val)}
                      >
                        <SelectTrigger data-testid="family-status-select">
                          <SelectValue placeholder="Selecciona su estatus actual" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="citizen">Ciudadano estadounidense</SelectItem>
                          <SelectItem value="permanent_resident">Residente permanente (Green Card)</SelectItem>
                          <SelectItem value="legal_temp">Estatus temporal legal (visa de trabajo, estudiante, etc.)</SelectItem>
                          <SelectItem value="pending">En proceso de regularización</SelectItem>
                          <SelectItem value="undocumented">Sin documentos / Indocumentado</SelectItem>
                          <SelectItem value="daca">DACA</SelectItem>
                          <SelectItem value="tps">TPS (Estatus de Protección Temporal)</SelectItem>
                          <SelectItem value="unknown">No lo sé</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Years in USA */}
                    <div className="space-y-3">
                      <Label className="text-navy-700 font-medium">¿Hace cuánto tiempo viven en Estados Unidos? *</Label>
                      <Select 
                        value={formData.family_years_in_usa} 
                        onValueChange={(val) => handleChange("family_years_in_usa", val)}
                      >
                        <SelectTrigger data-testid="family-years-select">
                          <SelectValue placeholder="Selecciona el tiempo aproximado" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="less_1">Menos de 1 año</SelectItem>
                          <SelectItem value="1_3">1 a 3 años</SelectItem>
                          <SelectItem value="3_5">3 a 5 años</SelectItem>
                          <SelectItem value="5_10">5 a 10 años</SelectItem>
                          <SelectItem value="10_20">10 a 20 años</SelectItem>
                          <SelectItem value="more_20">Más de 20 años</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Citizen or resident question */}
                    <div className="space-y-3">
                      <Label className="text-navy-700 font-medium">¿Alguno de ellos es ciudadano o residente permanente?</Label>
                      <RadioGroup
                        value={formData.family_citizen_or_resident}
                        onValueChange={(val) => handleChange("family_citizen_or_resident", val)}
                        className="flex flex-wrap gap-3"
                      >
                        <div className="flex items-center space-x-2 p-2 px-4 rounded-lg border border-navy-200 hover:border-navy-300">
                          <RadioGroupItem value="citizen" id="cor-citizen" />
                          <Label htmlFor="cor-citizen">Sí, ciudadano</Label>
                        </div>
                        <div className="flex items-center space-x-2 p-2 px-4 rounded-lg border border-navy-200 hover:border-navy-300">
                          <RadioGroupItem value="resident" id="cor-resident" />
                          <Label htmlFor="cor-resident">Sí, residente</Label>
                        </div>
                        <div className="flex items-center space-x-2 p-2 px-4 rounded-lg border border-navy-200 hover:border-navy-300">
                          <RadioGroupItem value="both" id="cor-both" />
                          <Label htmlFor="cor-both">Ambos</Label>
                        </div>
                        <div className="flex items-center space-x-2 p-2 px-4 rounded-lg border border-navy-200 hover:border-navy-300">
                          <RadioGroupItem value="none" id="cor-none" />
                          <Label htmlFor="cor-none">Ninguno</Label>
                        </div>
                      </RadioGroup>
                    </div>

                    {/* Petition question */}
                    <div className="space-y-4">
                      <div className="flex items-start gap-3 p-4 bg-purple-50 rounded-lg border border-purple-200">
                        <Checkbox
                          id="family-petition"
                          checked={formData.family_has_petition}
                          onCheckedChange={(checked) => handleChange("family_has_petition", checked)}
                        />
                        <div>
                          <Label htmlFor="family-petition" className="text-purple-900 font-medium cursor-pointer">
                            ¿Tu familiar ha iniciado una petición migratoria para ti?
                          </Label>
                          <p className="text-xs text-purple-700 mt-1">
                            Por ejemplo: Formulario I-130 (Petición de Familiar Extranjero) u otro proceso similar
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Warning for undocumented family */}
                    {(formData.family_legal_entry === "illegal" || formData.family_current_status === "undocumented") && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 bg-amber-50 rounded-lg border border-amber-300"
                      >
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm text-amber-800 font-medium">Información importante</p>
                            <p className="text-sm text-amber-700 mt-1">
                              Tener familiares en situación irregular en EE.UU. puede generar preguntas adicionales 
                              durante tu entrevista consular. Nuestro equipo te ayudará a prepararte para responder 
                              de manera adecuada. <strong>La honestidad es fundamental</strong> en el proceso migratorio.
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="space-y-3 pt-4 border-t border-navy-100">
                <Label className="text-navy-700 font-medium">Estado civil *</Label>
                <Select 
                  value={formData.marital_status} 
                  onValueChange={(val) => handleChange("marital_status", val)}
                >
                  <SelectTrigger data-testid="marital-status-select">
                    <SelectValue placeholder="Selecciona tu estado civil" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Soltero(a)</SelectItem>
                    <SelectItem value="married">Casado(a)</SelectItem>
                    <SelectItem value="divorced">Divorciado(a)</SelectItem>
                    <SelectItem value="widowed">Viudo(a)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Section 1: Employment */}
          {currentSection === 1 && (
            <div className="space-y-6">
              <div className="space-y-3">
                <Label className="text-navy-700 font-medium">Situación laboral actual *</Label>
                <Select 
                  value={formData.employment_status} 
                  onValueChange={(val) => handleChange("employment_status", val)}
                >
                  <SelectTrigger data-testid="employment-status-select">
                    <SelectValue placeholder="Selecciona tu situación laboral" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employed">Empleado</SelectItem>
                    <SelectItem value="self_employed">Independiente/Negocio propio</SelectItem>
                    <SelectItem value="unemployed">Desempleado</SelectItem>
                    <SelectItem value="retired">Jubilado</SelectItem>
                    <SelectItem value="student">Estudiante</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label className="text-navy-700 font-medium">Ingreso mensual (USD) *</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-navy-400" />
                  <Input
                    type="number"
                    placeholder="2000"
                    value={formData.monthly_income}
                    onChange={(e) => handleChange("monthly_income", parseFloat(e.target.value) || 0)}
                    className="pl-10"
                    data-testid="monthly-income-input"
                  />
                </div>
                <p className="text-sm text-navy-500">Ingresa tu ingreso mensual aproximado en dólares</p>
              </div>
            </div>
          )}

          {/* Section 2: Property & Travel */}
          {currentSection === 2 && (
            <div className="space-y-6">
              <div className="space-y-4">
                <Label className="text-navy-700 font-medium">¿Posees propiedades (casa, terreno, vehículo)?</Label>
                <RadioGroup
                  value={formData.owns_property ? "yes" : "no"}
                  onValueChange={(val) => handleChange("owns_property", val === "yes")}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="property-yes" />
                    <Label htmlFor="property-yes">Sí</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="property-no" />
                    <Label htmlFor="property-no">No</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-3">
                <Label className="text-navy-700 font-medium">¿Cuántos viajes internacionales has realizado?</Label>
                <Select 
                  value={(formData.previous_travels || 0).toString()} 
                  onValueChange={(val) => handleChange("previous_travels", parseInt(val))}
                >
                  <SelectTrigger data-testid="previous-travels-select">
                    <SelectValue placeholder="Selecciona una opción" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Ninguno</SelectItem>
                    <SelectItem value="1">1-2 viajes</SelectItem>
                    <SelectItem value="3">3-5 viajes</SelectItem>
                    <SelectItem value="6">Más de 5 viajes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Section 3: Purpose & History */}
          {currentSection === 3 && (
            <div className="space-y-6">
              <div className="space-y-3">
                <Label className="text-navy-700 font-medium">Propósito del viaje *</Label>
                <Select 
                  value={formData.travel_purpose} 
                  onValueChange={(val) => handleChange("travel_purpose", val)}
                >
                  <SelectTrigger data-testid="travel-purpose-select">
                    <SelectValue placeholder="Selecciona el propósito" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tourism">Turismo</SelectItem>
                    <SelectItem value="business">Negocios</SelectItem>
                    <SelectItem value="medical">Tratamiento médico</SelectItem>
                    <SelectItem value="visit_family">Visitar familia/amigos</SelectItem>
                    <SelectItem value="events">Eventos/Conferencias</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Travel Companions Section */}
              <div className="p-5 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border border-indigo-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-navy-900">Grupo de Viaje</h3>
                    <p className="text-xs text-navy-500">¿Quiénes viajarán contigo?</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Traveling alone? */}
                  <div className="space-y-3">
                    <Label className="text-navy-700 font-medium">¿Viajarás solo(a)?</Label>
                    <RadioGroup
                      value={formData.traveling_alone ? "yes" : "no"}
                      onValueChange={(val) => {
                        handleChange("traveling_alone", val === "yes");
                        if (val === "yes") {
                          handleChange("num_travelers", 1);
                          handleChange("travelers", []);
                        }
                      }}
                      className="flex gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="yes" id="alone-yes" />
                        <Label htmlFor="alone-yes">Sí, solo(a)</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="no" id="alone-no" />
                        <Label htmlFor="alone-no">No, con acompañantes</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Companions details */}
                  <AnimatePresence>
                    {!formData.traveling_alone && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="space-y-4 overflow-hidden"
                      >
                        {/* Number of travelers */}
                        <div className="space-y-2">
                          <Label className="text-navy-700 font-medium">¿Cuántas personas viajarán en total (incluyéndote)?</Label>
                          <Select 
                            value={(formData.num_travelers || 1).toString()} 
                            onValueChange={(val) => {
                              const num = parseInt(val);
                              handleChange("num_travelers", num);
                              // Adjust travelers array
                              const currentTravelers = formData.travelers || [];
                              if (num - 1 > currentTravelers.length) {
                                const newTravelers = [...currentTravelers];
                                for (let i = currentTravelers.length; i < num - 1; i++) {
                                  newTravelers.push({ relationship: "", age: "", name: "" });
                                }
                                handleChange("travelers", newTravelers);
                              } else {
                                handleChange("travelers", currentTravelers.slice(0, num - 1));
                              }
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecciona" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="2">2 personas</SelectItem>
                              <SelectItem value="3">3 personas</SelectItem>
                              <SelectItem value="4">4 personas</SelectItem>
                              <SelectItem value="5">5 personas</SelectItem>
                              <SelectItem value="6">6 personas</SelectItem>
                              <SelectItem value="7">7 o más personas</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Travelers list */}
                        {formData.travelers && formData.travelers.length > 0 && (
                          <div className="space-y-3">
                            <Label className="text-navy-700 font-medium">Información de los acompañantes:</Label>
                            {formData.travelers.map((traveler, index) => (
                              <motion.div
                                key={index}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                className="p-4 bg-white rounded-lg border border-indigo-200 space-y-3"
                              >
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium text-indigo-700">
                                    Acompañante {index + 1}
                                  </span>
                                </div>
                                <div className="grid sm:grid-cols-3 gap-3">
                                  <div>
                                    <Label className="text-xs text-navy-500">Parentesco *</Label>
                                    <Select 
                                      value={traveler.relationship}
                                      onValueChange={(val) => {
                                        const newTravelers = [...formData.travelers];
                                        newTravelers[index].relationship = val;
                                        handleChange("travelers", newTravelers);
                                      }}
                                    >
                                      <SelectTrigger className="mt-1">
                                        <SelectValue placeholder="Selecciona" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="spouse">Cónyuge</SelectItem>
                                        <SelectItem value="child">Hijo(a)</SelectItem>
                                        <SelectItem value="parent">Padre/Madre</SelectItem>
                                        <SelectItem value="sibling">Hermano(a)</SelectItem>
                                        <SelectItem value="partner">Pareja</SelectItem>
                                        <SelectItem value="friend">Amigo(a)</SelectItem>
                                        <SelectItem value="other">Otro</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div>
                                    <Label className="text-xs text-navy-500">Edad *</Label>
                                    <Select 
                                      value={traveler.age}
                                      onValueChange={(val) => {
                                        const newTravelers = [...formData.travelers];
                                        newTravelers[index].age = val;
                                        handleChange("travelers", newTravelers);
                                      }}
                                    >
                                      <SelectTrigger className="mt-1">
                                        <SelectValue placeholder="Edad" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="0-5">0-5 años (bebé/preescolar)</SelectItem>
                                        <SelectItem value="6-12">6-12 años (primaria)</SelectItem>
                                        <SelectItem value="13-17">13-17 años (secundaria)</SelectItem>
                                        <SelectItem value="18-25">18-25 años</SelectItem>
                                        <SelectItem value="26-35">26-35 años</SelectItem>
                                        <SelectItem value="36-50">36-50 años</SelectItem>
                                        <SelectItem value="51-65">51-65 años</SelectItem>
                                        <SelectItem value="65+">65+ años</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div>
                                    <Label className="text-xs text-navy-500">Nombre (opcional)</Label>
                                    <Input
                                      className="mt-1"
                                      placeholder="Nombre"
                                      value={traveler.name || ""}
                                      onChange={(e) => {
                                        const newTravelers = [...formData.travelers];
                                        newTravelers[index].name = e.target.value;
                                        handleChange("travelers", newTravelers);
                                      }}
                                    />
                                  </div>
                                </div>
                              </motion.div>
                            ))}
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* Minor children section */}
              {(formData.marital_status === "married" || formData.marital_status === "divorced") && (
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <Baby className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <Label className="text-amber-900 font-medium">¿Tienes hijos menores de edad?</Label>
                      <RadioGroup
                        value={formData.has_minor_children ? "yes" : "no"}
                        onValueChange={(val) => handleChange("has_minor_children", val === "yes")}
                        className="flex gap-4 mt-2"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="yes" id="minors-yes" />
                          <Label htmlFor="minors-yes">Sí</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="no" id="minors-no" />
                          <Label htmlFor="minors-no">No</Label>
                        </div>
                      </RadioGroup>
                    </div>
                  </div>

                  <AnimatePresence>
                    {formData.has_minor_children && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="space-y-4 overflow-hidden"
                      >
                        <div className="space-y-3">
                          <Label className="text-navy-700 font-medium">¿Viajarán todos tus hijos contigo?</Label>
                          <RadioGroup
                            value={formData.children_traveling}
                            onValueChange={(val) => handleChange("children_traveling", val)}
                            className="space-y-2"
                          >
                            <div className="flex items-center space-x-3 p-3 rounded-lg border border-navy-200 hover:border-navy-300">
                              <RadioGroupItem value="all" id="children-all" />
                              <Label htmlFor="children-all" className="cursor-pointer">
                                <span className="font-medium">Sí, todos viajarán</span>
                              </Label>
                            </div>
                            <div className="flex items-center space-x-3 p-3 rounded-lg border border-navy-200 hover:border-navy-300">
                              <RadioGroupItem value="some" id="children-some" />
                              <Label htmlFor="children-some" className="cursor-pointer">
                                <span className="font-medium">Solo algunos viajarán</span>
                              </Label>
                            </div>
                            <div className="flex items-center space-x-3 p-3 rounded-lg border border-navy-200 hover:border-navy-300">
                              <RadioGroupItem value="none" id="children-none" />
                              <Label htmlFor="children-none" className="cursor-pointer">
                                <span className="font-medium">Ninguno viajará (se quedan en mi país)</span>
                              </Label>
                            </div>
                          </RadioGroup>
                        </div>

                        {(formData.children_traveling === "some" || formData.children_traveling === "none") && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="space-y-3"
                          >
                            <Label className="text-navy-700 font-medium">¿Con quién se quedarán los hijos que no viajen?</Label>
                            <Select 
                              value={formData.children_staying_with}
                              onValueChange={(val) => handleChange("children_staying_with", val)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Selecciona" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="other_parent">Con el otro padre/madre</SelectItem>
                                <SelectItem value="grandparents">Con los abuelos</SelectItem>
                                <SelectItem value="other_family">Con otros familiares</SelectItem>
                                <SelectItem value="guardian">Con un tutor legal</SelectItem>
                              </SelectContent>
                            </Select>
                            
                            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm text-blue-800">
                              <Info className="w-4 h-4 inline mr-2" />
                              <strong>Importante:</strong> Si tienes hijos que no viajarán, esto demuestra lazos fuertes con tu país, 
                              lo cual es positivo para tu caso. El oficial puede preguntar sobre ellos durante la entrevista.
                            </div>
                          </motion.div>
                        )}

                        {/* Warning for school-age children traveling */}
                        {formData.travelers?.some(t => t.relationship === "child" && (t.age === "6-12" || t.age === "13-17")) && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="p-4 bg-amber-50 rounded-lg border border-amber-200"
                          >
                            <div className="flex items-start gap-3">
                              <GraduationCap className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="text-sm text-amber-800 font-medium">Niños en edad escolar</p>
                                <p className="text-sm text-amber-700 mt-1">
                                  Si viajas con hijos en edad escolar, el oficial consular puede preguntar:
                                </p>
                                <ul className="text-sm text-amber-700 mt-2 list-disc list-inside space-y-1">
                                  <li>¿Por qué viajan durante el período escolar?</li>
                                  <li>¿Tienen permiso de la escuela?</li>
                                  <li>¿Cuánto tiempo estarán ausentes?</li>
                                </ul>
                                <p className="text-sm text-amber-800 mt-2">
                                  <strong>Recomendación:</strong> Viajar durante vacaciones escolares o tener una carta de la escuela.
                                </p>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <Label className="text-navy-700 font-medium">¿Has sido negado anteriormente para una visa de USA?</Label>
                    <RadioGroup
                      value={formData.was_denied_before ? "yes" : "no"}
                      onValueChange={(val) => handleChange("was_denied_before", val === "yes")}
                      className="flex gap-4 mt-2"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="yes" id="denied-yes" />
                        <Label htmlFor="denied-yes">Sí</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="no" id="denied-no" />
                        <Label htmlFor="denied-no">No</Label>
                      </div>
                    </RadioGroup>
                  </div>
                </div>
              </div>

              {/* FOIA Section - Shows when user was denied before */}
              <AnimatePresence>
                {formData.was_denied_before && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="p-5 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <FileSearch className="w-6 h-6 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-heading text-lg font-bold text-navy-900 mb-2">
                            ¿Sabías que puedes conocer las razones exactas de tu negación?
                          </h3>
                          <p className="text-navy-600 text-sm mb-4">
                            A través de una solicitud <strong>FOIA (Freedom of Information Act)</strong>, puedes obtener 
                            los registros oficiales de tu entrevista consular y entender qué causó la negación de tu visa.
                          </p>
                          
                          <div className="bg-white/70 rounded-lg p-4 mb-4">
                            <h4 className="font-semibold text-navy-800 mb-2 flex items-center gap-2">
                              <Info className="w-4 h-4 text-blue-600" />
                              ¿Qué obtendrás con FOIA?
                            </h4>
                            <ul className="space-y-1.5 text-sm text-navy-600">
                              <li className="flex items-start gap-2">
                                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                                Las notas del oficial consular sobre tu caso
                              </li>
                              <li className="flex items-start gap-2">
                                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                                Las razones específicas bajo la sección 214(b)
                              </li>
                              <li className="flex items-start gap-2">
                                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                                Información para preparar mejor tu próxima solicitud
                              </li>
                            </ul>
                          </div>

                          <div className="flex flex-col sm:flex-row gap-3">
                            <Button
                              type="button"
                              onClick={() => setShowFOIAModal(true)}
                              className="bg-blue-600 hover:bg-blue-700 text-white"
                              data-testid="request-foia-btn"
                            >
                              <FileSearch className="w-4 h-4 mr-2" />
                              Solicitar información FOIA
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => window.open(`${API}/foia/info`, '_blank')}
                              className="border-blue-300 text-blue-700 hover:bg-blue-50"
                            >
                              <Info className="w-4 h-4 mr-2" />
                              Más información
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="space-y-3">
                <Label className="text-navy-700 font-medium">Información adicional (opcional)</Label>
                <Textarea
                  placeholder="Agrega cualquier información relevante sobre tu situación..."
                  value={formData.additional_info}
                  onChange={(e) => handleChange("additional_info", e.target.value)}
                  className="min-h-[100px]"
                  data-testid="additional-info-textarea"
                />
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t border-navy-200">
            <Button
              variant="outline"
              onClick={prevSection}
              disabled={currentSection === 0}
              className="px-6"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Anterior
            </Button>

            {currentSection < sections.length - 1 ? (
              <Button
                onClick={nextSection}
                className="bg-gold-600 hover:bg-gold-700 text-white px-6"
              >
                Siguiente
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-6"
                data-testid="submit-questionnaire-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    Enviar Cuestionario
                    <CheckCircle2 className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            )}
          </div>
        </motion.div>
      </div>

      {/* FOIA Request Modal */}
      <AnimatePresence>
        {showFOIAModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
            onClick={() => setShowFOIAModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-navy-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <FileSearch className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl font-bold text-navy-900">
                      Solicitud FOIA
                    </h2>
                    <p className="text-sm text-navy-500">
                      Completa los datos de tu negación
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-5">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    <strong>¿Qué es FOIA?</strong> La Ley de Libertad de Información te permite solicitar 
                    los registros oficiales de tu entrevista. Un asesor te contactará para guiarte en el proceso.
                  </p>
                </div>

                <div className="space-y-3">
                  <Label className="text-navy-700 font-medium flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Fecha aproximada de la negación *
                  </Label>
                  <Input
                    type="date"
                    value={foiaFormData.denial_date}
                    onChange={(e) => setFoiaFormData({ ...foiaFormData, denial_date: e.target.value })}
                    data-testid="foia-denial-date"
                  />
                </div>

                <div className="space-y-3">
                  <Label className="text-navy-700 font-medium flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Consulado donde fue la entrevista *
                  </Label>
                  <Select 
                    value={foiaFormData.consulate} 
                    onValueChange={(val) => setFoiaFormData({ ...foiaFormData, consulate: val })}
                  >
                    <SelectTrigger data-testid="foia-consulate-select">
                      <SelectValue placeholder="Selecciona el consulado" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ciudad_juarez">Ciudad Juárez</SelectItem>
                      <SelectItem value="guadalajara">Guadalajara</SelectItem>
                      <SelectItem value="hermosillo">Hermosillo</SelectItem>
                      <SelectItem value="matamoros">Matamoros</SelectItem>
                      <SelectItem value="merida">Mérida</SelectItem>
                      <SelectItem value="mexico_city">Ciudad de México</SelectItem>
                      <SelectItem value="monterrey">Monterrey</SelectItem>
                      <SelectItem value="nogales">Nogales</SelectItem>
                      <SelectItem value="nuevo_laredo">Nuevo Laredo</SelectItem>
                      <SelectItem value="tijuana">Tijuana</SelectItem>
                      <SelectItem value="otro">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-3">
                  <Label className="text-navy-700 font-medium">
                    Razón de la negación (si la conoces)
                  </Label>
                  <Select 
                    value={foiaFormData.denial_reason} 
                    onValueChange={(val) => setFoiaFormData({ ...foiaFormData, denial_reason: val })}
                  >
                    <SelectTrigger data-testid="foia-denial-reason-select">
                      <SelectValue placeholder="Selecciona si conoces la razón" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="214b">214(b) - No demostré vínculos suficientes</SelectItem>
                      <SelectItem value="221g">221(g) - Procesamiento administrativo</SelectItem>
                      <SelectItem value="not_sure">No estoy seguro</SelectItem>
                      <SelectItem value="other">Otra razón</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-3">
                  <Label className="text-navy-700 font-medium">
                    Detalles adicionales (opcional)
                  </Label>
                  <Textarea
                    placeholder="Describe brevemente qué sucedió en tu entrevista o cualquier información relevante..."
                    value={foiaFormData.additional_details}
                    onChange={(e) => setFoiaFormData({ ...foiaFormData, additional_details: e.target.value })}
                    className="min-h-[80px]"
                    data-testid="foia-additional-details"
                  />
                </div>

                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-800">
                    <strong>Próximos pasos:</strong> Después de enviar esta solicitud, un asesor se pondrá 
                    en contacto contigo en las próximas 24-48 horas para continuar con el proceso.
                  </p>
                </div>
              </div>

              <div className="p-6 border-t border-navy-100 flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowFOIAModal(false)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleFOIARequest}
                  disabled={foiaLoading || !foiaFormData.denial_date || !foiaFormData.consulate}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="submit-foia-btn"
                >
                  {foiaLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Enviar Solicitud
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </DashboardLayout>
  );
};

export default QuestionnaireStep;
